import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { CalendarDays, ChevronDown } from "lucide-react";
import SmartDatePicker from "../../../components/SmartDatePicker";
import LocationSearchInput from "../../../components/LocationSearchInput";

export type MoveSubcategory = { id: string | number; name: string };

type MoveSearchBarProps = {
  categoryId: string;
  subcategories: MoveSubcategory[];
  /** Move hasn't launched, so the bar previews the search instead of running it */
  disabled?: boolean;
  /** Note shown under the bar while it is disabled */
  disabledNote?: string;
};

const LABEL = "block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]";
const CELL = "border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] lg:border-b-0 lg:border-r-[1px]";

export default function MoveSearchBar({ categoryId, subcategories, disabled, disabledNote }: MoveSearchBarProps) {
  const history = useHistory();
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    let url = `/workspaces-search?addressContains=${encodeURIComponent(pickupLocation)}`;
    if (categoryId) url += `&categoryId=${encodeURIComponent(categoryId)}`;
    if (selectedSubcategoryId) url += `&subCategoryId=${encodeURIComponent(selectedSubcategoryId)}`;
    if (dateRange) {
      if (dateRange.startDate && dateRange.endDate) {
        url += `&startDate=${encodeURIComponent(dateRange.startDate)}&endDate=${encodeURIComponent(dateRange.endDate)}`;
      } else if (dateRange.specificDate && dateRange.specificDate.length > 0) {
        url += `&specificDate=${encodeURIComponent(dateRange.specificDate.join(","))}`;
      }
    }
    if (dropoffLocation.trim()) url += `&dropoffLocation=${encodeURIComponent(dropoffLocation.trim())}`;
    history.push(url);
  };

  // Google Places autocomplete, restyled to sit flush inside the search bar cell
  const locationInput = (label: string, value: string, onChange: (value: string) => void, placeholder: string) => (
    <div className={CELL}>
      <label className={LABEL}>{label}</label>
      <div className="pt-1">
        <LocationSearchInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          ariaLabel={label}
          disabled={disabled}
          className="!h-5 !rounded-none !border-0 !bg-transparent !px-0 [&>svg]:!mr-2 [&>svg]:!size-3.5 [&>svg]:!text-[#017bef]"
          inputClassName="!text-[14px] !font-normal !text-[#0a0a0a] placeholder:!text-[#717182]"
        />
      </div>
    </div>
  );

  return (
    <section className="bg-white px-[16px] py-20 sm:px-8" aria-label="Car rental search">
      <form
        onSubmit={handleSearch}
        className={`mx-auto grid max-w-[1376px] grid-cols-1 overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] shadow-[0_8px_40px_0_rgba(0,0,0,0.08)] sm:grid-cols-2 lg:grid-cols-5 ${
          disabled ? "bg-[#f8fbff]" : "bg-white"
        }`}
      >
        {locationInput("Pickup Location", pickupLocation, setPickupLocation, "Where are you picking up?")}
        {locationInput("Drop-off Location", dropoffLocation, setDropoffLocation, "Where are you dropping off?")}

        <div className={CELL}>
          <label className={LABEL}>Pickup Date</label>
          <div className="pt-1">
            {disabled ? (
              <p className="flex h-5 items-center gap-2 text-[14px] leading-5 text-[#717182]">
                <CalendarDays className="size-3.5 text-[#017bef]" />
                Pickup
              </p>
            ) : (
              <SmartDatePicker
                value={dateRange}
                onChange={setDateRange}
                placeholder="Pickup"
                className="h-5 w-full border-0 bg-transparent p-0 text-[14px] shadow-none [&>svg]:mr-2 [&>svg]:size-3.5 [&>svg]:text-[#017bef]"
              />
            )}
          </div>
        </div>

        <div className={CELL}>
          <label className={LABEL} htmlFor="move-vehicle-type">
            Vehicle Type
          </label>
          <div className="relative pt-1">
            <select
              id="move-vehicle-type"
              value={selectedSubcategoryId}
              onChange={e => setSelectedSubcategoryId(e.target.value)}
              disabled={disabled}
              className="h-5 w-full cursor-pointer appearance-none border-0 bg-transparent p-0 pr-6 text-[14px] leading-5 text-[#0a0a0a] outline-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
            >
              <option value="">All vehicles</option>
              {subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute bottom-[3px] right-0 size-3.5 text-[#717182]" />
          </div>
        </div>

        <div className="flex items-center px-[16px] py-[12px] sm:col-span-2 lg:col-span-1">
          <button
            type="submit"
            disabled={disabled}
            className="w-full rounded-[14px] bg-[#017bef] px-[20px] py-[12px] text-[14px] font-semibold leading-5 text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)] transition-colors hover:bg-[#0062c0] disabled:cursor-not-allowed disabled:bg-[#017bef]/40 disabled:drop-shadow-none"
          >
            Find a Car
          </button>
        </div>
      </form>
      {disabled && disabledNote && (
        <p className="mx-auto max-w-[1376px] pt-[12px] text-center text-[14px] leading-5 text-[#717182]">
          {disabledNote}
        </p>
      )}
    </section>
  );
}
